import { useCallback } from "react";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import type { WeekData } from "../models";
import { computeDayTargetContext, computeLocationTotalTarget, countCoverageNursesOnLocation, shiftNursesForDay } from "./locationTarget";
import type { TargetWarningProps } from "./WarningDialog";

type TargetCheckParams = {
	/** The caller's schedule weeks; `weekIndex`, `dayNo` and `shiftIndex` are positions in them. */
	weeks: WeekData[];
	locationName: string | null | undefined;
	weekIndex: number;
	dayNo: number;
	shiftIndex: number;
	isActual: boolean;
	/** How many nurses the add puts on the location at once (multi-add adds N in one publish). */
	addCount?: number;
};

export type TargetCheckResult = TargetWarningProps & { exceeded: boolean };

const NOT_EXCEEDED: TargetCheckResult = { exceeded: false, current: 0, target: 0, location: "" };

/** Every add flow before multi-add placed exactly one nurse. */
const SINGLE_ADD_COUNT = 1;

/**
 * Whether adding `addCount` nurses (one unless told otherwise) to a location's shift would push coverage
 * past the location's staffing target. `current` is the projected count INCLUDING the nurses being
 * added, `target` is the location TOTAL required (summed across same-named DepartmentLocation variants, no role/job filter)
 * resolved against the day's census via the shared ratio-ladder math. Feeds the staffing-target step
 * of the nurse-warning cascade (see [[WarningDialog]]); reused by every add/approve flow.
 */
export const useStaffingTargetCheck = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { isHospitalView } = useDepartmentFlags();

	return useCallback(
		// DEFAULT: addCount falls back to a single nurse so every pre-multi-add caller keeps its exact behaviour.
		({ weeks, locationName, weekIndex, dayNo, shiftIndex, isActual, addCount = SINGLE_ADD_COUNT }: TargetCheckParams): TargetCheckResult => {
			// Department opted out of the over-staffing warning entirely.
			if (chosenDepartment.hideOverstaffingWarning) return NOT_EXCEEDED;
			if (!locationName) return NOT_EXCEEDED;

			const matchingLocations = chosenDepartment.locations.filter((loc) => loc.location === locationName);
			if (!matchingLocations.length) return NOT_EXCEEDED;

			const day = weeks[weekIndex]?.weeklyShifts[shiftIndex]?.days[dayNo];
			if (!day) return NOT_EXCEEDED;

			// Display grouping no longer feeds this: the over-staffing target is the same number for
			// every user of the department, whatever census grouping each has selected.
			const context = computeDayTargetContext(weeks, chosenDepartment, weekIndex, dayNo, isHospitalView);
			if (!context) return NOT_EXCEEDED;

			const target = computeLocationTotalTarget(matchingLocations, context, chosenDepartment.statsMode);
			if (target <= 0) return NOT_EXCEEDED;

			const projected = countCoverageNursesOnLocation(shiftNursesForDay(day, isActual), locationName) + addCount;

			return { exceeded: projected > target, current: projected, target, location: locationName };
		},
		[chosenDepartment, isHospitalView],
	);
};
